import React from 'react';
import { Link } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import GuideLayout, { GuidePhoto } from './GuideLayout';
import photoCemetery from '../../../img/guide-kwiaty-cmentarz.webp';
import photoChrysanthemums from '../../../img/guide-kwiaty-chryzantemy.webp';

// Poradnik sezonowy (1-2 listopada) pod frazy wyszukiwane przez zakłady i kwiaciarnie: „sprzedaż zniczy”,
// „sprzedaż zniczy na Wszystkich Świętych”, „znicze hurtowo”, „jak sprzedawać znicze przy cmentarzu”.
// Pisany tak, żeby był aktualny co roku - bez dat konkretnego roku.

const PHOTOS = {
    cemetery: {
        src: photoCemetery,
        alt: 'Setki zapalonych zniczy na cmentarzu Srebrzysko w Gdańsku',
        caption: 'Znicze na cmentarzu Srebrzysko w Gdańsku',
        author: 'Ludomił Sawicki',
        source: 'https://unsplash.com/photos/a-large-group-of-colorful-lit-candles-sitting-on-top-of-a-table-r5K_apW4R5o',
    },
    chrysanthemums: {
        src: photoChrysanthemums,
        alt: 'Pomarańczowe chryzantemy z bliska',
        caption: 'Chryzantemy - obok zniczy najważniejszy towar przed 1 listopada',
        author: 'Kat',
        source: 'https://unsplash.com/photos/orange-chrysanthemums-bloom-beautifully-in-a-garden-TGe_zgkUoq4',
    },
};

const TIMELINE = [
    {
        title: 'Wrzesień - zamówienia hurtowe',
        text: 'Ustal z hurtownią asortyment i terminy dostaw. Im później, tym mniejszy wybór wzorów i większe ryzyko, że zabraknie popularnych wkładów.',
    },
    {
        title: 'Początek października - chryzantemy',
        text: 'Zarezerwuj chryzantemy u ogrodnika lub w hurtowni kwiatowej. Ustal, ile doniczek odbierasz od razu, a ile na ostatnie dni przed świętem.',
    },
    {
        title: 'Połowa października - ekspozycja i zamówienia',
        text: 'Wystaw znicze i stroiki w zakładzie, ogłoś na Facebooku przyjmowanie zamówień i usługę sprzątania grobów. Część klientów kupuje wcześniej, żeby uniknąć tłoku.',
    },
    {
        title: 'Ostatni tydzień - stoisko przy cmentarzu',
        text: 'Największy ruch jest w ostatnich dniach przed 1 listopada. Przygotuj stoisko, zapas towaru, oświetlenie i zadaszenie na wypadek deszczu.',
    },
    {
        title: '1-2 listopada - Wszystkich Świętych i Zaduszki',
        text: 'Sprzedaż trwa do wieczora 2 listopada. Pilnuj zapasu wkładów i zapalniczek - to najczęściej kupowane „na ostatnią chwilę”.',
    },
    {
        title: 'Po świętach - podsumowanie',
        text: 'Spisz, czego zabrakło, a czego zostało za dużo. Te notatki to najlepsza podstawa do zamówień na przyszły rok.',
    },
];

const ASSORTMENT = [
    { title: 'Znicze szklane', text: 'Klasyka w wielu kolorach i rozmiarach - od małych lampionów po duże, ozdobne znicze z metalowymi daszkami.' },
    { title: 'Wkłady do zniczy', text: 'Parafinowe i olejowe, o różnym czasie palenia. Klienci często kupują same wkłady do zniczy, które już stoją na grobie.' },
    { title: 'Znicze elektryczne i solarne', text: 'Nie gasną na wietrze i świecą długo. Dobry wybór dla osób, które rzadko odwiedzają grób.' },
    { title: 'Chryzantemy w doniczkach', text: 'Symbol Wszystkich Świętych. Warto mieć kilka rozmiarów i kolorów - od małych doniczek po duże, wielokwiatowe.' },
    { title: 'Wrzosy, stroiki i wiązanki', text: 'Kompozycje z jedliny, szyszek i suszu stoją na grobie aż do zimy. Wrzosy w doniczkach to tańsza alternatywa dla chryzantem.' },
    { title: 'Drobiazgi „na ostatnią chwilę”', text: 'Zapalniczki, zapałki, długie zapalarki, rękawiczki i worki na śmieci. Tanie, a sprzedają się przy każdym zakupie.' },
];

const SALES_TIPS = [
    { title: 'Gotowe zestawy', text: 'Znicz z wkładem, chryzantema ze zniczem, zestaw „na dwa groby”. Klient wybiera szybciej, a Ty sprzedajesz więcej przy jednym zakupie.' },
    { title: 'Widoczne ceny', text: 'Ceny na każdej półce i każdym wzorze. Przy tłoku nikt nie chce pytać - po prostu idzie do sąsiedniego stoiska.' },
    { title: 'Płatność kartą i BLIK', text: 'Coraz więcej osób nie ma przy sobie gotówki. Terminal płatniczy przy stoisku to wprost więcej sprzedaży.' },
    { title: 'Dobre miejsce', text: 'Przy wejściu na cmentarz lub przy parkingu - tam, gdzie ludzie przechodzą, zanim dotrą do grobu.' },
    { title: 'Przygotowanie na pogodę', text: 'Namiot, oświetlenie na wieczór i folia na towar. Na przełomie października i listopada zmrok zapada wcześnie, a deszcz to norma.' },
    { title: 'Ekologia', text: 'Wkłady do wielokrotnie używanych zniczy i znicze solarne to coraz częstsze pytanie klientów. Warto mieć je w ofercie.' },
];

const SERVICES = [
    {
        title: 'Sprzątanie i dekorowanie grobów',
        text: 'Dla rodzin, które mieszkają daleko: umycie pomnika, wymiana kwiatów i zniczy, zdjęcie grobu po zakończeniu pracy. Zamówienia przyjmuj z wyprzedzeniem.',
    },
    {
        title: 'Wymiana zniszczonych tabliczek',
        text: (
            <>
                Przed 1 listopada rodziny widzą wyblakłe tabliczki na krzyżach. Nową tabliczkę metodą sublimacji
                zrobisz w kilka minut - zobacz <Link to='/jak-zrobic-tabliczke-nagrobna'>jak zrobić tabliczkę nagrobną</Link>.
            </>
        ),
    },
    {
        title: 'Zamówienia z odbiorem',
        text: 'Przyjmuj zamówienia telefonicznie lub przez Facebooka i przygotuj je do odbioru w zakładzie. Klient omija kolejkę, Ty planujesz zapas towaru.',
    },
];

const FORMALITIES = [
    { title: 'Zgoda na handel przy cmentarzu', text: 'Stoisko przy cmentarzu lub na jego terenie zwykle wymaga zgody zarządcy cmentarza albo gminy. Zapytaj o nią z wyprzedzeniem - miejsc bywa niewiele.' },
    { title: 'Kasa fiskalna i rozliczenia', text: 'Sprzedaż na stoisku podlega tym samym zasadom co w zakładzie. Kwestie kasy fiskalnej i rozliczeń omów wcześniej ze swoim księgowym.' },
    { title: 'Porządek po sprzedaży', text: 'Zabierz kartony, folie i niesprzedany towar. Czyste stoisko to też wizytówka Twojego zakładu.' },
];

const FAQ = [
    {
        question: 'Kiedy zamówić znicze na Wszystkich Świętych?',
        answer: 'Zamówienia hurtowe najlepiej złożyć we wrześniu, a chryzantemy zarezerwować na początku października. Im później, tym mniejszy wybór wzorów i większe ryzyko braków w ostatnim tygodniu przed 1 listopada.',
    },
    {
        question: 'Jaki asortyment przygotować na Wszystkich Świętych?',
        answer: 'Podstawą są znicze szklane w kilku rozmiarach, wkłady do zniczy, chryzantemy w doniczkach, wrzosy i stroiki. Warto dodać znicze elektryczne lub solarne oraz drobiazgi kupowane na ostatnią chwilę: zapalniczki, zapałki i rękawiczki.',
    },
    {
        question: 'Czy na sprzedaż zniczy przy cmentarzu potrzebne jest pozwolenie?',
        answer: 'Zwykle tak - handel przy cmentarzu lub na jego terenie wymaga zgody zarządcy cmentarza albo gminy. Zasady różnią się w zależności od miejsca, dlatego zapytaj o nie z kilkutygodniowym wyprzedzeniem.',
    },
    {
        question: 'Czym różni się Wszystkich Świętych od Zaduszek?',
        answer: 'Wszystkich Świętych (1 listopada) to święto ku czci wszystkich świętych. Dzień Zaduszny, czyli Zaduszki lub Wszystkich Zmarłych (2 listopada), to dzień modlitwy za zmarłych. W Polsce groby bliskich odwiedza się w oba te dni.',
    },
    {
        question: 'Jak długo pali się wkład do znicza?',
        answer: 'Zależy od wielkości i rodzaju wkładu - od kilkunastu godzin do kilku dni. Czas palenia jest zwykle podany na opakowaniu. Klienci, którzy rzadko odwiedzają grób, chętnie wybierają wkłady o długim czasie palenia albo znicze solarne.',
    },
];

export default function SprzedazZniczy() {
    return (
        <GuideLayout
            path='/sprzedaz-zniczy-wszystkich-swietych'
            title='Sprzedaż zniczy na Wszystkich Świętych - poradnik dla zakładów pogrzebowych | Nekrodruk.pl'
            description='Jak przygotować sprzedaż zniczy, wkładów i chryzantem na Wszystkich Świętych i Zaduszki? Kalendarz przygotowań, asortyment, stoisko przy cmentarzu, dodatkowe usługi i formalności - poradnik dla zakładów pogrzebowych i kwiaciarni.'
            keywords='sprzedaż zniczy, sprzedaż zniczy Wszystkich Świętych, znicze hurtowo, stoisko ze zniczami przy cmentarzu, chryzantemy na Wszystkich Świętych, wkłady do zniczy, Zaduszki, Wszystkich Zmarłych, sprzątanie grobów usługa'
            heading={<>Sprzedaż zniczy na Wszystkich Świętych<em> - jak się przygotować?</em></>}
            lead='1 i 2 listopada to najważniejsze dni w roku dla sprzedaży zniczy, chryzantem i stroików. Podpowiadamy, kiedy zamówić towar, co mieć w ofercie, jak przygotować stoisko przy cmentarzu i jakie dodatkowe usługi zaproponować rodzinom.'
            actions={
                <>
                    <HashLink smooth to='/sprzedaz-zniczy-wszystkich-swietych#kalendarz' className='guide_btn guide_btn--light'>
                        Kalendarz przygotowań
                    </HashLink>
                    <HashLink smooth to='/sprzedaz-zniczy-wszystkich-swietych#uslugi' className='guide_btn guide_btn--ghost'>
                        Dodatkowe usługi
                    </HashLink>
                </>
            }
            faq={FAQ}
        >
            <section className='guide_section' id='kalendarz'>
                <div className='guide_inner'>
                    <span className='guide_kicker'>Kalendarz</span>
                    <h2 className='guide_title'>Kiedy zacząć przygotowania do Wszystkich Świętych?</h2>
                    <p className='guide_lead'>
                        Sprzedaż przed 1 listopada trwa kilka dni, ale przygotowania zaczynają się dużo wcześniej. Dobre
                        planowanie to mniej braków w ostatnim tygodniu i mniej niesprzedanego towaru po świętach.
                    </p>
                    <GuidePhoto photo={PHOTOS.cemetery} />
                    <ol className='guide_steps guide_steps--wide'>
                        {TIMELINE.map((step) => (
                            <li key={step.title}>
                                <strong>{step.title}</strong>
                                <span>{step.text}</span>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            <section className='guide_section guide_section--soft'>
                <div className='guide_inner'>
                    <span className='guide_kicker'>Asortyment</span>
                    <h2 className='guide_title'>Co mieć w ofercie na 1 listopada?</h2>
                    <GuidePhoto photo={PHOTOS.chrysanthemums} />
                    <div className='guide_cards'>
                        {ASSORTMENT.map((item) => (
                            <article key={item.title} className='guide_card'>
                                <h3>{item.title}</h3>
                                <p>{item.text}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className='guide_section'>
                <div className='guide_inner'>
                    <span className='guide_kicker'>Stoisko</span>
                    <h2 className='guide_title'>Jak sprzedawać znicze przy cmentarzu?</h2>
                    <ul className='guide_checklist'>
                        {SALES_TIPS.map((tip) => (
                            <li key={tip.title}>
                                <strong>{tip.title}</strong>
                                <span>{tip.text}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className='guide_section guide_section--soft' id='uslugi'>
                <div className='guide_inner'>
                    <span className='guide_kicker'>Więcej niż znicze</span>
                    <h2 className='guide_title'>Dodatkowe usługi na Wszystkich Świętych</h2>
                    <p className='guide_lead'>
                        Przed 1 listopada rodziny odwiedzają groby częściej niż przez cały rok - i wtedy widzą, czego grób
                        potrzebuje. To dobry moment, żeby zaproponować więcej niż sam towar.
                    </p>
                    <div className='guide_cards'>
                        {SERVICES.map((service) => (
                            <article key={service.title} className='guide_card'>
                                <h3>{service.title}</h3>
                                <p>{service.text}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className='guide_section'>
                <div className='guide_inner'>
                    <span className='guide_kicker'>Formalności</span>
                    <h2 className='guide_title'>O czym pamiętać przed otwarciem stoiska</h2>
                    <ul className='guide_checklist'>
                        {FORMALITIES.map((item) => (
                            <li key={item.title}>
                                <strong>{item.title}</strong>
                                <span>{item.text}</span>
                            </li>
                        ))}
                    </ul>
                    <p className='guide_note'>
                        Chcesz lepiej doradzać klientom przy stoisku? Zobacz też,{' '}
                        <Link to='/jakie-kwiaty-na-cmentarz'>jakie kwiaty na cmentarz</Link> wybrać o każdej porze roku.
                    </p>
                </div>
            </section>

            <section className='guide_section guide_section--soft'>
                <div className='guide_inner'>
                    <div className='guide_callout'>
                        <div>
                            <h2>
                                Nowe tabliczki przed 1 listopada<em> - gotowe w kilka minut</em>
                            </h2>
                            <p>
                                Wyblakła tabliczka na krzyżu to pierwsze, co rodzina zauważa przy grobie. W Nekrodruku
                                przygotujesz projekt nowej tabliczki ze zdjęciem w kilka minut - gotowy do{' '}
                                <Link to='/jak-zrobic-tabliczke-nagrobna'>wykonania metodą sublimacji</Link>.
                            </p>
                        </div>
                        <div className='guide_actions'>
                            <Link to='/register' className='guide_btn guide_btn--light'>
                                Załóż konto
                            </Link>
                            <HashLink smooth to='/#Contact' className='guide_btn guide_btn--ghost'>
                                Napisz do nas
                            </HashLink>
                        </div>
                    </div>
                </div>
            </section>
        </GuideLayout>
    );
}
